import type { TableServiceConfigDto, WizardStatusDto } from '@onboarding/application/dtos';

export const WizardActions = {
  WIZARD_STATUS_LOADED: 'WIZARD_STATUS_LOADED',
  NEXT_STEP: 'NEXT_STEP',
  SAVE_SUCCESS: 'SAVE_SUCCESS',
  SAVE_FAILED: 'SAVE_FAILED',
  PREV_STEP: 'PREV_STEP',
  UPDATE_PROFILE: 'UPDATE_PROFILE',
  RECONCILIATION_STARTED: 'RECONCILIATION_STARTED',
  RECONCILIATION_RESULT: 'RECONCILIATION_RESULT',
  DEFAULTS_APPLIED: 'DEFAULTS_APPLIED',
  DEFAULTS_FAILED: 'DEFAULTS_FAILED',
  WIZARD_COMPLETED: 'WIZARD_COMPLETED',
  FINAL_ERROR: 'FINAL_ERROR',
  GOTO_STEP: 'GOTO_STEP',
} as const;

export type WizardAction =
  | { type: typeof WizardActions.WIZARD_STATUS_LOADED; payload: WizardStatusDto }
  | { type: typeof WizardActions.NEXT_STEP }
  | { type: typeof WizardActions.SAVE_SUCCESS }
  | { type: typeof WizardActions.SAVE_FAILED; payload: { error: string } }
  | { type: typeof WizardActions.PREV_STEP }
  | { type: typeof WizardActions.UPDATE_PROFILE; payload: { stepData: Record<string, unknown> } }
  | { type: typeof WizardActions.RECONCILIATION_STARTED }
  | { type: typeof WizardActions.RECONCILIATION_RESULT; payload: { resolved: string[]; failed: string[] } }
  | { type: typeof WizardActions.DEFAULTS_APPLIED }
  | { type: typeof WizardActions.DEFAULTS_FAILED; payload: { error: string } }
  | { type: typeof WizardActions.WIZARD_COMPLETED }
  | { type: typeof WizardActions.FINAL_ERROR; payload: { error: string } }
  | { type: typeof WizardActions.GOTO_STEP; payload: { step: number } };

export type WizardSavingStatus = 'idle' | 'saving' | 'error';

export type WizardState = {
  currentStep: number;
  totalSteps: number;
  completedSteps: string[];
  pendingSteps: string[];
  savingStatus: WizardSavingStatus;
  savingError: string | null;
  wizardCompleted: boolean;
  restaurantType: string | null;
  tableServiceConfig: TableServiceConfigDto | null;
  isLoaded: boolean;
};

export const wizardInitialState: WizardState = {
  currentStep: 0,
  totalSteps: 7,
  completedSteps: [],
  pendingSteps: [],
  savingStatus: 'idle',
  savingError: null,
  wizardCompleted: false,
  restaurantType: null,
  tableServiceConfig: null,
  isLoaded: false,
};

/**
 * Reducer for onboarding wizard state machine.
 * Handles step navigation, saving states, and error recovery.
 */
export const wizardReducer = (state: WizardState, action: WizardAction): WizardState => {
  switch (action.type) {
    case WizardActions.WIZARD_STATUS_LOADED:
      return {
        ...state,
        currentStep: action.payload.currentStep,
        totalSteps: action.payload.totalSteps,
        completedSteps: action.payload.completedSteps,
        pendingSteps: action.payload.pendingSteps,
        wizardCompleted: action.payload.wizardCompleted,
        restaurantType: action.payload.restaurantType,
        tableServiceConfig: action.payload.tableServiceConfig,
        isLoaded: true,
      };

    case WizardActions.NEXT_STEP:
      return {
        ...state,
        savingStatus: 'saving',
        savingError: null,
      };

    case WizardActions.SAVE_SUCCESS:
      return {
        ...state,
        currentStep: Math.min(state.currentStep + 1, state.totalSteps),
        savingStatus: 'idle',
        savingError: null,
      };

    case WizardActions.SAVE_FAILED:
      return {
        ...state,
        savingStatus: 'error',
        savingError: action.payload.error,
      };

    case WizardActions.PREV_STEP:
      return {
        ...state,
        currentStep: Math.max(0, state.currentStep - 1),
        savingStatus: 'idle',
        savingError: null,
      };

    case WizardActions.UPDATE_PROFILE:
      return { ...state };

    case WizardActions.RECONCILIATION_STARTED:
      return {
        ...state,
        savingStatus: 'saving',
      };

    case WizardActions.RECONCILIATION_RESULT:
      return {
        ...state,
        pendingSteps: action.payload.failed,
        savingStatus: 'idle',
      };

    case WizardActions.DEFAULTS_APPLIED:
      return {
        ...state,
        savingStatus: 'idle',
      };

    case WizardActions.DEFAULTS_FAILED:
      return {
        ...state,
        savingStatus: 'error',
        savingError: action.payload.error,
      };

    case WizardActions.WIZARD_COMPLETED:
      return {
        ...state,
        wizardCompleted: true,
        savingStatus: 'idle',
      };

    case WizardActions.FINAL_ERROR:
      return {
        ...state,
        savingStatus: 'error',
        savingError: action.payload.error,
      };

    case WizardActions.GOTO_STEP:
      return {
        ...state,
        currentStep: Math.max(0, Math.min(action.payload.step, state.totalSteps)),
        savingStatus: 'idle',
        savingError: null,
      };

    default:
      return state;
  }
};
